'use client';

import { Checkbox } from '@ethsign/ui';
import { ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';

import classNames from 'classnames';

import { ReactNode, useEffect, useState } from 'react';

import { DataTablePagination } from '@/components/Table/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  className?: string;
  rowSelection?: { onRowSelect: (data: { [i: number]: boolean }) => void };
  pagination?: {
    pageSize: number;
    onChange?: (page: number) => void;
    total?: number;
    pageIndex?: number;
    manualPagination?: boolean;
  };
  emptyText?: ReactNode;
  getTable?: (table: any) => void;
}

export function DataTable<TData, TValue>({
  columns,
  pagination,
  data,
  className,
  rowSelection,
  emptyText,
  getTable
}: DataTableProps<TData, TValue>) {
  const newColumns = [...columns];
  if (rowSelection) {
    newColumns.unshift({
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            row.toggleSelected(!!value);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false
    });
  }
  const [selectedData, setSelectedData] = useState({});
  const [paginationInfo, setPaginationInfo] = useState<any>({
    ...(pagination || {})
  });

  const table = useReactTable({
    data,
    columns: newColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: !pagination || pagination?.manualPagination ? undefined : getPaginationRowModel(),
    onRowSelectionChange: setSelectedData,
    state: {
      rowSelection: selectedData,
      pagination: pagination ? paginationInfo : undefined
    },
    onPaginationChange: setPaginationInfo,
    pageCount: pagination?.manualPagination ? Math.ceil((pagination?.total || 0) / pagination?.pageSize) : undefined,
    manualPagination: pagination?.manualPagination
  });

  useEffect(() => {
    rowSelection?.onRowSelect(selectedData);
  }, [selectedData]);
  useEffect(() => {
    pagination?.onChange && pagination?.onChange(paginationInfo.pageIndex + 1);
  }, [paginationInfo]);
  useEffect(() => {
    if (pagination?.pageIndex == undefined) return;
    setPaginationInfo({ pageIndex: pagination?.pageIndex, pageSize: pagination?.pageSize });
  }, [pagination?.pageIndex]);
  useEffect(() => {
    getTable && getTable(table);
  }, [table]);

  return (
    <div className={classNames('bg-white w-full overflow-hidden', className)}>
      <Table>
        <TableHeader className="bg-gray-25 [&_tr]:divide-x [&_tr]:divide-gray-100 text-gray-500">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, i) => {
                return (
                  <TableHead key={i}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, i) => (
              <TableRow key={i} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell, j) => (
                  <TableCell key={j}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={newColumns.length} className="h-24 text-center text-gray-400 opacity-60 text-xs">
                <div className="flex items-center justify-center w-full">{emptyText || 'No results.'}</div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination && (
        <div className="w-full">
          <DataTablePagination table={table} />
        </div>
      )}
    </div>
  );
}
